import type { Agency, Merchant, MerchantStore } from '../../../common/types';
import { useGetStoresForAgencyQuery, useGetStoresForMerchantQuery, useGetStoresQuery } from '../store.api-slice';
import AddOrEditApiConfig from '../components/DefaultApiConfig/AddOrEditApiConfig';
import AddStore from '../components/AddStore';
import ApproveStore from '../components/ApproveStore';
import Breadcrumb from '../../../components/Breadcrumb';
import DeleteStore from '../components/DeleteStore';
import EditStore from '../components/EditStore';
import { getAgencyMerchantStoreBasedOnScopeAndRole } from '../../../common/helpers';
import { Link } from 'react-router-dom';
import ListStores from '../components/ListStores';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { OwnerType } from '../../../common/constants';
import Pagination from '../../../components/Pagination';
import SearchStore from '../components/SearchStore';
import { skipToken } from '@reduxjs/toolkit/query';
import SuccessMessage from '../../../components/success-message';
import { useAuth } from '../../../app/hooks/useAuth';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  scope?: 'all' | 'agency' | 'merchant';
  filter?: { pending: boolean };
  agency?: Agency;
  merchant?: Merchant;
}

const ListStoresPage: React.FC<IProps> = ({ filter, agency, merchant, scope = 'all' }) => {
  const { user } = useAuth();
  const [editingItem, setEditingItem] = useState<MerchantStore | null>(null);
  const [deletingItem, setDeletingItem] = useState<MerchantStore | null>(null);
  const [selectedStore, setSelectedStore] = useState<MerchantStore | null>(null);
  const [approvingItem, setApprovingItem] = useState<MerchantStore | null>(null);
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openApproveModal, setOpenApproveModal] = useState<boolean>(false);
  const [openApiConfigModal, setOpenApiConfigModal] = useState<boolean>(false);
  const parentContext = getAgencyMerchantStoreBasedOnScopeAndRole({ loggedInUser: user, scope, agency, merchant });

  const [page, setPage] = useState<number>(1);
  const [term, setTerm] = useState<string>('');
  const { t } = useTranslation();

  const { data: merchantStores, isLoading } =
    scope === 'agency'
      ? useGetStoresForAgencyQuery(agency != null ? { page, term, id: agency.id } : skipToken)
      : scope === 'merchant'
      ? useGetStoresForMerchantQuery(merchant != null ? { page, term, id: merchant.id } : skipToken)
      : useGetStoresQuery({ page, term, pendingOnly: Boolean(filter?.pending) });

  const pageTitle =
    scope === 'agency' && agency != null
      ? `List of Stores for ${agency?.name}`
      : scope === 'merchant' && merchant != null
      ? `List of Stores for ${merchant?.name}`
      : filter?.pending === true
      ? 'List of Pending Stores'
      : 'List of Stores';

  const handleSearch = (term: string): void => {
    setTerm(term);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const toggleAddModal = (): void => {
    setOpenAddModal(!openAddModal);
  };

  const toggleEditModal = (): void => {
    if (openEditModal) {
      setEditingItem(null);
    }
    setOpenEditModal(!openEditModal);
  };

  const toggleDeleteModal = (): void => {
    if (openDeleteModal) {
      setDeletingItem(null);
    }
    setOpenDeleteModal(!openDeleteModal);
  };

  const toggleApproveModal = (): void => {
    if (openApproveModal) {
      setApprovingItem(null);
    }
    setOpenApproveModal(!openApproveModal);
  };

  const toggleApiConfigModal = (): void => {
    if (openApiConfigModal) {
      setSelectedStore(null);
    }
    setOpenApiConfigModal(!openApiConfigModal);
  };

  const handleSelectStore = (item: MerchantStore | null): void => {
    setSelectedStore(item);
  };

  const handleEditingItem = (item: MerchantStore | null): void => {
    setEditingItem(item);
  };

  const handleDeletingItem = (item: MerchantStore | null): void => {
    setDeletingItem(item);
  };

  const handleApprovingItem = (item: MerchantStore | null): void => {
    setApprovingItem(item);
  };

  return (
    <div className="">
      <div className="pb-4">
        <h1>{t('stores.list.title')}</h1>
        <Breadcrumb
          items={[{ label: t('common.adminBreadcrumbLabel') }, { label: t('stores.list.breadcrumbLabel') }]}
        />
      </div>
      <div className="w-full flex flex-col md:flex-row md:justify-between md:items-center space-y-2">
        <h1 className="text-2xl">{pageTitle}</h1>
        <div className="flex justify-items-center md:justify-between md:items-center gap-x-2">
          <SearchStore handleSearch={handleSearch} />
          {filter?.pending === true && (
            <Link to="/admin/stores">
              <button className="text-gray ml-4 px-5 py-2 rounded-lg bg-gray-50">List Stores</button>
            </Link>
          )}
          {filter?.pending !== true && (
            <>
              <Link to="/admin/stores/pending-requests">
                <button className="text-gray ml-4 px-5 py-2 rounded-lg bg-gray-50">Pending Stores</button>
              </Link>
              <button onClick={toggleAddModal} className="text-white ml-4 px-5 py-2 rounded-lg bg-[#1D8A43]">
                {t('stores.add.title')}
              </button>
            </>
          )}
        </div>
      </div>

      {selectedStore != null && (
        <AddOrEditApiConfig
          ownerType={OwnerType.STORE}
          ownerEntity={selectedStore}
          isOpen={openApiConfigModal}
          toggleModal={toggleApiConfigModal}
        />
      )}

      <AddStore
        isOpen={openAddModal}
        toggleModal={toggleAddModal}
        parentContext={parentContext}
        agencyInContext={agency}
        merchantInContext={merchant}
      />

      <EditStore isOpen={openEditModal} toggleEditModal={toggleEditModal} item={editingItem} />

      <DeleteStore isOpen={openDeleteModal} toggleDeleteModal={toggleDeleteModal} item={deletingItem} />
      <ApproveStore isOpen={openApproveModal} toggleApproveModal={toggleApproveModal} item={approvingItem} />
      {merchantStores?.data == null ||
        (merchantStores.data.length === 0 && <SuccessMessage message={t('stores.list.empty')} type="error" />)}
      {merchantStores?.data != null && merchantStores.data.length > 0 && (
        <>
          <ListStores
            items={merchantStores.data}
            toggleEditModal={toggleEditModal}
            toggleDeleteModal={toggleDeleteModal}
            toggleApproveModal={toggleApproveModal}
            toggleApiConfigModal={toggleApiConfigModal}
            handleEditingItem={handleEditingItem}
            handleDeletingItem={handleDeletingItem}
            handleApprovingItem={handleApprovingItem}
            handleSelectItem={handleSelectStore}
          />
          <Pagination pageData={merchantStores.meta} handlePageChange={setPage} />
        </>
      )}
    </div>
  );
};

export default ListStoresPage;
